import { Spinner } from 'react-bootstrap';
import React, { useEffect, useState } from 'react';
import { updateProfile } from 'firebase/auth';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { auth, firestore } from './FirebaseConfig';
import { useAuth } from './AuthContext';
import { API_BASE } from '../apiSession';
import './WelcomeName.css';

// The first thing a new user is asked, before anything else in the product.
//
// Register never set displayName on the auth record — it derived a name from
// the email prefix for a log line and threw it away. So every email-and-
// password account had displayName === null permanently, and the app has three
// places that read it: the dashboard greets "Good evening, there", the nav
// falls back to the raw email address, and Account says "Not set". Nothing was
// broken enough to report, it was just impersonal forever.
//
// Someone who signed in with Google already has a name from their provider. It
// is prefilled and they confirm it, because asking a question we already know
// the answer to is worse than not asking.
//
// This blocks the app deliberately — it is one field and it is the name we
// address the person by everywhere else. What it must never do is lock someone
// out: if the check itself fails, the app opens.

const SETTINGS_PATH = (uid) => doc(firestore, 'users', uid, 'profile', 'accountSettings');


/**
 * Establishes who this account belongs to, from what we already know.
 *
 * ── Why this writes the CANONICAL record ───────────────────────────────────
 *
 * This is the first screen of the product and the person is answering "is this
 * your name?" about themselves. That is the definition of a canonical write,
 * so it goes to /api/profile/identity — which sets Postgres Profile, the
 * source of truth, as well as the active resume's header.
 *
 * It used to PUT /api/profile/initialData, which is the RESUME EDITOR's
 * endpoint and deliberately non-canonical: somebody may open the editor to fix
 * a friend's CV, and that must not rewrite whose account it is. Sharing it
 * meant a brand-new account got a resume header and an empty Profile — so the
 * one store everything else treats as the person was never established at
 * signup, and Account had to fall back to reading a resume to know their name.
 *
 * Only writes fields that are currently empty. This prompt also appears for
 * accounts created before it existed — people who may already have a resume
 * with a deliberately different name on it — and overwriting that with their
 * sign-in name would be editing their document without being asked to.
 */
export async function seedResumeContact(currentUser, name) {
  try {
    const token = await currentUser.getIdToken();
    const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };

    const existing = await fetch(`${API_BASE}/api/profile/identity`, { headers })
      .then(r => (r.ok ? r.json() : null))
      .catch(() => null);

    // No read, no write. Seeding blind was survivable when this only touched a
    // resume header; now that it establishes the source of truth, guessing
    // could overwrite a real name with a sign-in one.
    if (!existing?.ok) return;
    const current = existing.identity || {};

    const patch = {};
    if (!current.name) patch.name = name;
    if (!current.email && currentUser.email) patch.email = currentUser.email;
    if (Object.keys(patch).length === 0) return;

    await fetch(`${API_BASE}/api/profile/identity`, {
      method: 'PUT',
      headers,
      body: JSON.stringify(patch),
    });
  } catch (err) {
    // Never surfaced: the name is saved either way, and this is a convenience.
    console.error('[welcome] could not seed contact details', err);
  }
}

const WelcomeName = ({ onDone }) => {
  const { currentUser } = useAuth();
  // Seeded at first render rather than filled by an effect.
  //
  // It used to start empty and get set in a useEffect, which meant a Google
  // user saw "Is this your name?" above an empty box for a frame before their
  // name appeared. Small, but it is the first screen of the product, and it
  // made a test fail intermittently for the honest reason that the field
  // really was empty at that moment.
  //
  // The effect also had currentUser in its dependencies, so any re-render that
  // produced a new user object would have wiped whatever the person had typed.
  const [name, setName] = useState(() => currentUser?.displayName || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const hadName = Boolean(currentUser?.displayName);

  const submit = async (e) => {
    e.preventDefault();
    const trimmed = name.trim().replace(/\s+/g, ' ');

    if (!trimmed) {
      setError('Please enter a name so we know what to call you.');
      return;
    }
    if (trimmed.length > 60) {
      setError('That is a bit long. A first name is plenty.');
      return;
    }

    setSaving(true);
    setError('');
    try {
      // Written to the auth record, because that is what the rest of the app
      // already reads. Mirroring it somewhere new would mean three sources of
      // one name and two of them going stale.
      await updateProfile(auth.currentUser, { displayName: trimmed });

      // Separately, a marker so this is never asked again — including on
      // another device, which is why it is not localStorage.
      await setDoc(
        SETTINGS_PATH(currentUser.uid),
        { displayNameConfirmed: true, displayNameConfirmedAt: new Date().toISOString() },
        { merge: true }
      );

      // Put it on the resume too, so the first thing they see in Design
      // Studio is not a form asking for what they just typed. Their email is
      // seeded the same way — it has been on the auth record since they
      // registered, and the profile panel was still showing "Email Not Set".
      //
      // Deliberately not awaited and deliberately unable to fail the save: the
      // name is already stored, and a resume that starts blank is a much
      // smaller problem than a user stuck on this screen.
      seedResumeContact(currentUser, trimmed);

      // No need to push the new name into AuthContext. updateProfile mutates
      // the local user object, and AppShell is not mounted yet — the gate is
      // rendering this instead of it. Flipping the gate mounts AppShell for
      // the first time, and it reads the name that is already there.
      onDone(trimmed);
    } catch (err) {
      console.error('[welcome] could not save the name', err);
      setError('That did not save. Check your connection and try again.');
      setSaving(false);
    }
  };

  return (
    // claude-theme: this dialog was built in the warm palette by hand — 55
    // hex literals copied out of claudeTheme.css. Reading the tokens instead
    // only works if the warm ones are in scope, and the class is also what
    // brings `body.dark-theme .claude-theme` with it. Without it the same
    // substitution would silently repaint the screen in the cool legacy set.
    <div className="wn-backdrop claude-theme" role="dialog" aria-modal="true" aria-labelledby="wn-title">
      <form className="wn-card" onSubmit={submit}>
        <h1 id="wn-title" className="wn-title">
          {hadName ? 'Is this your name?' : 'What should we call you?'}
        </h1>
        <p className="wn-sub">
          {hadName
            ? 'We got this from your sign-in. Change it if it is not what you go by.'
            : 'We use it to address you in the app and nowhere else.'}
        </p>

        <label className="wn-label" htmlFor="wn-input">Name</label>
        <input
          id="wn-input"
          className="wn-input"
          value={name}
          onChange={(e) => { setName(e.target.value); setError(''); }}
          placeholder="Alex"
          autoComplete="name"
          // The whole screen is this one field; focusing anything else would
          // just be a keystroke the user has to spend.
          autoFocus
          disabled={saving}
          aria-invalid={Boolean(error)}
          aria-describedby={error ? 'wn-error' : undefined}
        />

        {error && <div id="wn-error" className="wn-error" role="alert">{error}</div>}

        <button className="wn-btn" type="submit" disabled={saving}>
          {saving ? 'Saving…' : hadName ? 'Yes, continue' : 'Continue'}
        </button>
      </form>
    </div>
  );
};

/**
 * Decides whether to ask, and renders the prompt if so.
 *
 * Renders nothing at all while it is still working out the answer — showing
 * the prompt for a moment and snatching it away is worse than a beat of
 * nothing, and would flash on every single page load for every user.
 */
export const WelcomeNameGate = ({ children }) => {
  const { currentUser } = useAuth();
  // checking | ask (the name) | done
  const [status, setStatus] = useState('checking');

  useEffect(() => {
    if (!currentUser) { setStatus('done'); return undefined; }
    let cancelled = false;

    (async () => {
      try {
        const snap = await getDoc(SETTINGS_PATH(currentUser.uid));
        if (cancelled) return;

        // Confirmed once means never again, even if they later clear the name.
        // Re-asking someone who deliberately blanked it would be nagging.
        const confirmed = snap.exists() && snap.data()?.displayNameConfirmed;
        if (!confirmed) { setStatus('ask'); return; }

        // The name is settled. Everything else is asked by the setup in the
        // chat (components/Setup, founder 2026-09-28), which leads with the
        // resume; the three joining screens it replaced are not shown.
        setStatus('done');
      } catch (err) {
        // A failed read must not hold the product hostage. Worst case we miss
        // the chance to ask; the Account page can still set a name.
        console.error('[welcome] could not check name status, continuing', err);
        if (!cancelled) setStatus('done');
      }
    })();

    return () => { cancelled = true; };
  }, [currentUser]);

  // A spinner, never nothing. The only thing awaited here is one Firestore
  // read, which is fast; the slow server read above no longer blocks this.
  if (status === 'checking') {
    return (
      <div className="loading-screen" role="status" aria-live="polite">
        <Spinner animation="border" variant="primary"><span className="visually-hidden">Loading…</span></Spinner>
      </div>
    );
  }
  if (status === 'ask') {
    // Then the app: the chat takes it from here (the setup in the chat).
    return <WelcomeName onDone={() => setStatus('done')} />;
  }
  return children;
};

export default WelcomeName;
